"use client";

import type * as React from "react";

import { useSlider } from "@components/composers/slider/Slider";
import { Button } from "@components/core/Button";
import ChevronLeft24 from "@ui/icons/24x24/chevron-left-24.svg";
import ChevronRight24 from "@ui/icons/24x24/chevron-right-24.svg";
import { cn } from "@ui/lib/utils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { useTranslations } from "next-intl";

interface SliderButtonProps extends React.ComponentProps<typeof Button> {
  pill?: boolean;
  ref?: React.RefObject<HTMLButtonElement>;
}

const SliderNext = ({ className, variant = "secondary", size = "md", ref, ...props }: SliderButtonProps) => {
  const { scrollNext, canScrollNext } = useSlider();
  const t = useTranslations("ClientKeys.Globals");

  return (
    <Button
      aria-label='Next slide'
      className={cn(className, "focus:z-2")}
      disabled={!canScrollNext}
      icon={<ChevronRight24 />}
      iconPosition={ICON_ALIGN_OPTIONS.AFTER}
      onClick={scrollNext}
      ref={ref}
      size={size}
      variant={variant}
      {...props}
    >
      <span className='sr-only'>{t("next_slide")}</span>
    </Button>
  );
};
SliderNext.displayName = "SliderNext";

const SliderPrevious = ({ className, variant = "secondary", size = "md", ref, ...props }: SliderButtonProps) => {
  const { scrollPrev, canScrollPrev } = useSlider();
  const t = useTranslations("ClientKeys.Globals");

  return (
    <Button
      aria-label='Previous slide'
      className={cn(className, "focus:z-2")}
      disabled={!canScrollPrev}
      icon={<ChevronLeft24 />}
      iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
      onClick={scrollPrev}
      ref={ref}
      size={size}
      variant={variant}
      {...props}
    >
      <span className='sr-only'>{t("previous_slide")}</span>
    </Button>
  );
};
SliderPrevious.displayName = "SliderPrevious";

interface SliderControlsProps extends React.HTMLAttributes<HTMLDivElement> {
  inverse?: boolean;
}
const SliderControls = ({ inverse = false, className, ...props }: SliderControlsProps) => {
  return (
    <div
      className={cn(
        "flex h-40 items-center rounded-full p-4",
        inverse ? "bg-overlay-light" : "bg-overlay-dark",
        className,
      )}
      {...props}
    >
      <SliderPrevious className='' size='pill' variant={inverse ? "ghost-inverse" : "secondary"} />
      <SliderNext className='' size='pill' variant={inverse ? "ghost-inverse" : "secondary"} />
    </div>
  );
};

SliderControls.displayName = "SliderControls";

export { SliderControls, SliderNext, SliderPrevious };
